import { useState, type MouseEvent } from 'react';
import { Check, Clock, GripVertical, MoreHorizontal, Trash2 } from 'lucide-react';
import type { Label, Task } from '../../../lib/tasksApi';
import { LinearLabelPill } from './LinearLabelPill';
import { formatEstimate } from '../../../lib/estimate';
import { formatShortDate } from './dueDate';
import { AssigneeEditor, DueDateEditor, PriorityEditor, StatusEditor } from './inlineEditors';
import { useDrag } from './dragContext';
import { Popover } from './Popover';
import { useDeleteTask } from '../../../hooks/useTasks';
import { useToast } from './Toast';
import { useI18n } from '../../../contexts/I18nContext';
import { useConfirm } from '../../ui/ConfirmProvider';

/**
 * CLE-174 / CLE-179 — a single Linear-style task row.
 *
 * Layout (left → right) with exact spec gaps: drag handle (hover), checkbox /
 * priority slot, identifier, status icon, flex-grow title, label pills,
 * estimate, due date, assignee avatar, created date. The checkbox is hidden until row hover
 * (or when selected) and visually replaces the priority icon, matching Linear.
 *
 * Rows are draggable (CLE-179): grabbing one moves it (or, when several are
 * checkbox-selected, all of them) between status groups. While a drag is in
 * flight the source row dims to 0.4 and a blue insertion line marks the drop
 * position above the hovered row.
 */

interface TaskRowProps {
  task: Task;
  selected: boolean;
  onToggleSelect: (id: string, shift: boolean) => void;
  onOpen: (id: string) => void;
  onLabelClick?: (label: Label) => void;
  /** Show the parent project chip — only meaningful on a cross-project list. */
  showProjectName?: boolean;
}

const MAX_LABELS = 2;

export function TaskRow({
  task,
  selected,
  onToggleSelect,
  onOpen,
  onLabelClick,
  showProjectName,
}: TaskRowProps) {
  // While any inline popover is open we suppress row-click navigation so
  // interacting with an editor doesn't also open the detail view.
  const [popoverOpen, setPopoverOpen] = useState(false);
  const drag = useDrag();
  const del = useDeleteTask();
  const toast = useToast();
  const { t } = useI18n();
  const confirm = useConfirm();

  async function handleDelete(close: () => void) {
    close();
    if (!(await confirm({ message: t('tasks.row.confirmDelete'), danger: true }))) return;
    del.mutate(task.id, {
      onSuccess: () => toast.show(t('tasks.row.deleted')),
      onError: () => toast.show(t('tasks.row.deleteFailed')),
    });
  }

  const isDragging = drag.state.active && drag.state.draggingIds.has(task.id);
  const showDropLine =
    drag.state.active &&
    drag.state.drop?.beforeId === task.id &&
    drag.state.drop?.status === task.statusId;

  function handleRowClick() {
    if (popoverOpen || drag.state.active) return;
    onOpen(task.id);
  }

  function handleCheckbox(e: MouseEvent) {
    e.stopPropagation();
    onToggleSelect(task.id, e.shiftKey);
  }

  const shownLabels = task.labels.slice(0, MAX_LABELS);
  const extraLabels = task.labels.length - shownLabels.length;

  return (
    <div
      draggable
      onDragStart={(e) => drag.onRowDragStart(task.id, task.statusId, e)}
      onDragEnd={drag.onRowDragEnd}
      onDragOver={(e) => drag.onRowDragOver(task.id, task.statusId, e)}
      onDrop={(e) => {
        e.preventDefault();
        drag.onDrop(task.statusId);
      }}
      onClick={handleRowClick}
      className={`group/row relative flex h-9 cursor-pointer items-center transition-colors duration-75 ${
        selected ? 'bg-brand-50' : 'hover:bg-gray-200'
      } ${isDragging ? 'opacity-40' : ''}`}
      style={{ paddingLeft: 8, paddingRight: 12 }}
      role="row"
      aria-grabbed={isDragging}
    >
      {/* Blue insertion line marking the drop position above this row. */}
      {showDropLine && (
        <span className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-0.5 bg-brand-600">
          <span className="absolute -left-0.5 -top-[3px] h-2 w-2 rounded-full bg-brand-600" />
        </span>
      )}

      {/* Drag handle — grip dots appear on hover, left of the checkbox. Hidden
          on phones: HTML5 drag needs a mouse and the invisible slot just stole
          12px of row width. */}
      <span
        className="hidden w-3 shrink-0 cursor-grab items-center justify-center text-gray-400 opacity-0 transition-opacity group-hover/row:opacity-100 active:cursor-grabbing sm:flex"
        onClick={(e) => e.stopPropagation()}
        aria-hidden="true"
      >
        <GripVertical size={12} />
      </span>

      {/* Checkbox (hover/selected) overlapping the priority icon slot — 24px. */}
      <div className="relative flex h-6 w-6 shrink-0 items-center justify-center">
        <button
          type="button"
          onClick={handleCheckbox}
          aria-label={selected ? `Deselectează ${task.title}` : `Selectează ${task.title}`}
          className={`absolute left-1 top-1/2 flex h-3.5 w-3.5 -translate-y-1/2 items-center justify-center rounded-[3px] border transition-opacity ${
            selected
              ? 'border-brand-600 bg-brand-600 opacity-100'
              : 'border-gray-400 opacity-0 group-hover/row:opacity-100'
          }`}
        >
          {selected && <Check size={11} className="text-fixed-white" />}
        </button>
        {/* Priority icon (16px) hides on hover so the checkbox can take its place. */}
        <div
          className={`flex h-4 w-4 items-center justify-center ${
            selected ? 'opacity-0' : 'group-hover/row:opacity-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <PriorityEditor task={task} onOpenChange={setPopoverOpen} />
        </div>
      </div>

      {/* Identifier — 13px/400 #6b7280, tabular nums. */}
      <span
        className="ml-1.5 hidden shrink-0 truncate font-mono text-[13px] tabular-nums text-gray-400 sm:inline"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {task.identifier ?? '—'}
      </span>

      {/* Status icon — 14px, gap 8 before, gap 6 after. */}
      <div className="ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        <StatusEditor task={task} onOpenChange={setPopoverOpen} />
      </div>

      {/* Title — 13px/400 #e5e5e5, line-height 1.4, ellipsis. */}
      <span className="ml-1.5 min-w-0 flex-1 truncate text-[13px] leading-[1.4] text-gray-800">
        {task.title}
      </span>

      {/* Project chip — same outline shape as the label pills, dimmer text so it
          doesn't compete with them. Passive: clicking it just opens the task. */}
      {showProjectName && task.projectName && (
        <span
          title={task.projectName}
          className="ml-3 hidden max-w-[140px] shrink-0 truncate rounded-full border border-gray-300 px-2 py-px text-[11px] font-medium text-gray-500 md:inline-block"
        >
          {task.projectName}
        </span>
      )}

      {/* Labels — gap 12 before. */}
      {task.labels.length > 0 && (
        <div className="ml-3 hidden shrink-0 items-center gap-1.5 md:flex">
          {shownLabels.map((l) => (
            <LinearLabelPill key={l.id} label={l} onClick={() => onLabelClick?.(l)} />
          ))}
          {extraLabels > 0 && (
            <span className="rounded-full border border-gray-300 px-2 py-px text-[11px] font-medium text-gray-500">
              +{extraLabels}
            </span>
          )}
        </div>
      )}

      {/* Estimate — immediately before the due date, so "cât durează" sits next
          to "până când". Read-only here (the sidebar and the board card own the
          editing); an unestimated task renders nothing rather than a dash, to
          keep the row quiet. */}
      {task.estimate != null && (
        <span
          title={t('tasks.row.estimate')}
          className="ml-2 hidden shrink-0 items-center gap-1 px-1.5 py-0.5 text-[12px] font-medium text-gray-500 sm:inline-flex"
        >
          <Clock size={12} />
          {formatEstimate(task.estimate)}
        </span>
      )}

      {/* Due date — gap 8 before. */}
      <div className="ml-2 hidden shrink-0 sm:block" onClick={(e) => e.stopPropagation()}>
        <DueDateEditor task={task} onOpenChange={setPopoverOpen} />
      </div>

      {/* Assignee — gap 6 before, 20px avatar. */}
      <div className="ml-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        <AssigneeEditor task={task} onOpenChange={setPopoverOpen} />
      </div>

      {/* Created date — gap 6 before, 12px/400 #6b7280. min-width keeps the
          column aligned across rows; nowrap stops "10 sept." from wrapping
          onto two lines (the old fixed w-10 was too narrow for 2-digit days). */}
      <span className="ml-1.5 hidden min-w-[3.5rem] shrink-0 whitespace-nowrap text-right text-[12px] text-gray-400 lg:inline">
        {formatShortDate(task.createdAt)}
      </span>

      {/* Row actions — kebab menu (⋯) revealed on hover (or while open), with a
          Delete action. Opening it suppresses row-click navigation just like the
          inline editors. Hidden on phones (no hover to reveal it): the title
          gets the width back and delete stays reachable via the detail page and
          the bulk-selection bar. */}
      <div className="ml-1.5 hidden shrink-0 sm:block" onClick={(e) => e.stopPropagation()}>
        <Popover
          width={160}
          align="end"
          onOpenChange={setPopoverOpen}
          trigger={({ open, toggle, ref }) => (
            <button
              type="button"
              ref={ref}
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              aria-label={t('tasks.row.menu')}
              className={`flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-opacity hover:bg-gray-200 hover:text-gray-800 ${
                open ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'
              }`}
            >
              <MoreHorizontal size={14} />
            </button>
          )}
        >
          {(close) => (
            <div className="py-1">
              <button
                type="button"
                onClick={() => handleDelete(close)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-red-500 hover:bg-gray-200"
              >
                <Trash2 size={13} /> {t('tasks.row.delete')}
              </button>
            </div>
          )}
        </Popover>
      </div>
    </div>
  );
}
